/**
 * Data transformation layer for bucketed charts: groups daily contributions into day / week /
 * month buckets and derives metric values for each bucket. GitHub only reports daily counts,
 * so every metric here is computed from those.
 *
 * To add a metric: add its name to `METRIC_NAMES` in options.ts and an entry to `METRICS`.
 */
import { CalendarDay } from '../../github/types';
import { MetricName } from '../options';
import {
    DAY_MS,
    MONTH_NAMES,
    MONTH_NAMES_LONG,
    formatLongDate,
    formatShortDate,
    toUtc,
} from './dates';
import { formatNumber } from './escape';

export type Bucketing = 'day' | 'week' | 'month';

export interface Bucket {
    /** First and last date in the bucket that has data (inclusive). */
    start: string;
    end: string;
    days: CalendarDay[];
}

export type ResolvedMetric = Exclude<MetricName, 'auto'>;

export interface MetricContext {
    /** This metric's value for the previous bucket (for running totals). */
    previous: number;
    /** Every day in the selected range, for metrics that look beyond the bucket. */
    allDays: CalendarDay[];
    /** Index in `allDays` of the bucket's last day. */
    endIndex: number;
}

export interface MetricDefinition {
    label: string;
    compute(days: CalendarDay[], context: MetricContext): number;
}

const TREND_WINDOW = 7;

const sum = (days: CalendarDay[]) => days.reduce((total, day) => total + day.count, 0);

export const METRICS: Record<ResolvedMetric, MetricDefinition> = {
    contributions: { label: 'Contributions', compute: (days) => sum(days) },
    activeDays: {
        label: 'Active days',
        compute: (days) => days.filter((day) => day.count > 0).length,
    },
    average: {
        label: 'Daily average',
        compute: (days) => (days.length ? Math.round((sum(days) / days.length) * 10) / 10 : 0),
    },
    best: {
        label: 'Best day',
        compute: (days) => days.reduce((best, day) => Math.max(best, day.count), 0),
    },
    cumulative: {
        label: 'Running total',
        compute: (days, { previous }) => previous + sum(days),
    },
    trend: {
        label: '7-day average',
        compute: (_days, { allDays, endIndex }) => {
            const window = allDays.slice(Math.max(0, endIndex - TREND_WINDOW + 1), endIndex + 1);
            return window.length ? Math.round((sum(window) / window.length) * 10) / 10 : 0;
        },
    },
};

/**
 * `auto`: bars show contributions; the line shows a 7-day trend for daily buckets (active days
 * per day would only ever be 0 or 1) and active days for weekly/monthly buckets.
 */
export const resolveMetric = (
    metric: MetricName,
    series: 'bar' | 'line',
    bucketing: Bucketing,
): ResolvedMetric => {
    if (metric !== 'auto') return metric;
    if (series === 'bar') return 'contributions';
    return bucketing === 'day' ? 'trend' : 'activeDays';
};

/** Days → daily bars for up to ~a month, weekly up to ~6 months, monthly beyond. */
export const chooseBucketing = (dayCount: number): Bucketing =>
    dayCount <= 31 ? 'day' : dayCount <= 190 ? 'week' : 'month';

const bucketKey = (date: string, bucketing: Bucketing): string => {
    if (bucketing === 'day') return date;
    if (bucketing === 'month') return date.slice(0, 7);
    // Weeks start on Sunday, like GitHub's calendar.
    const time = toUtc(date);
    return new Date(time - new Date(time).getUTCDay() * DAY_MS).toISOString().slice(0, 10);
};

export const bucketDays = (days: CalendarDay[], bucketing: Bucketing): Bucket[] => {
    const buckets: Bucket[] = [];
    let currentKey = '';
    for (const day of days) {
        const key = bucketKey(day.date, bucketing);
        if (key !== currentKey) {
            buckets.push({ start: day.date, end: day.date, days: [] });
            currentKey = key;
        }
        const bucket = buckets[buckets.length - 1];
        bucket.days.push(day);
        bucket.end = day.date;
    }
    return buckets;
};

export const metricValues = (buckets: Bucket[], metric: ResolvedMetric): number[] => {
    const allDays = buckets.flatMap((bucket) => bucket.days);
    const values: number[] = [];
    let endIndex = -1;
    for (const bucket of buckets) {
        endIndex += bucket.days.length;
        values.push(
            METRICS[metric].compute(bucket.days, {
                previous: values[values.length - 1] ?? 0,
                allDays,
                endIndex,
            }),
        );
    }
    return values;
};

/** Short axis label, e.g. `Sep 7`, `12`, `Jan '26`. */
export const bucketLabel = (
    bucket: Bucket,
    bucketing: Bucketing,
    previous: Bucket | undefined,
    spansYears: boolean,
): string => {
    const date = new Date(toUtc(bucket.start));
    const month = date.getUTCMonth();
    if (bucketing === 'month')
        return spansYears && month === 0
            ? `${MONTH_NAMES[month]} '${bucket.start.slice(2, 4)}`
            : MONTH_NAMES[month];
    const newMonth = !previous || previous.start.slice(0, 7) !== bucket.start.slice(0, 7);
    return newMonth || bucketing === 'week'
        ? `${MONTH_NAMES[month]} ${date.getUTCDate()}`
        : String(date.getUTCDate());
};

/** Long label for tooltips, e.g. `September 2026`, `Week of Sep 7, 2026`. */
export const bucketTitle = (bucket: Bucket, bucketing: Bucketing): string => {
    if (bucketing === 'day') return formatLongDate(bucket.start);
    if (bucketing === 'week') return `Week of ${formatShortDate(bucket.start)}`;
    const date = new Date(toUtc(bucket.start));
    return `${MONTH_NAMES_LONG[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

/** Compact axis numbers: 950, 1.5k, 12k, 2.5 (for averages). */
export const formatCompact = (value: number): string => {
    if (Math.abs(value) >= 1000) {
        const thousands = value / 1000;
        return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1).replace(/\.0$/, '')}k`;
    }
    return Number.isInteger(value) ? formatNumber(value) : value.toFixed(1);
};
